import apiClient from '../api';

// Type definitions matching backend DTOs
export interface CreateLearningPathDto {
  user_input: string;  // AI generates metadata from this
  target_role_or_skill: string;
  difficulty_level?: string;
  targeted_audience?: string;
  industry?: string;
  content_language?: string;
  // Language for the LP wrapper text — required on the backend; the form
  // enforces this with a `required` rule on the Select.
  lp_language: string;
}

export interface LearningPathDto {
  _id: string;
  orchestration_id: string;
  title: string;
  slug: string;
  description: string;
  target_role_or_skill: string;
  targeted_audience?: string;
  difficulty_progression: string;
  outcomes: string[];
  tags: string[];
  suggestions: string[];
  banner_image_url?: string;
  thumbnail_image_url?: string;
  certificate_template_id?: string | null; // chosen certificate template; empty = default
  courses: CourseInPathDto[];
  total_courses: number;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  published_at?: Date;
  total_enrolled: number;
  total_completed: number;
  created_by: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  rating?: number;
  review_count?: number;
}

export interface CourseInPathDto {
  course_id: string;
  order: number;
  course_title: string;
  course_description?: string;
  difficulty_level?: string;
  rationale: string;  // AI explanation
}

export interface OrchestrationStageStatus {
  status: 'not_started' | 'processing' | 'completed' | 'failed';
  start_time?: Date;
  end_time?: Date;
  retry_count: number;
  error_details?: string;
}

export interface LearningPathOrchestrationDto {
  _id: string;
  user_input: string;
  target_role_or_skill: string;
  difficulty_level: string;
  targeted_audience?: string;
  industry?: string;
  content_language: string;
  path_title?: string;
  path_slug?: string;
  description?: string;
  outcomes?: string[];
  tags?: string[];
  suggestions?: string[];
  banner_image_url?: string;
  thumbnail_image_url?: string;
  selected_courses?: SelectedCourseDto[];
  no_courses_found: boolean;
  no_courses_reason?: string;
  learning_path_id?: string;
  metadata_generation_status: OrchestrationStageStatus;
  image_generation_status: OrchestrationStageStatus;
  course_selection_status: OrchestrationStageStatus;
  created_by: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface SelectedCourseDto {
  course_id: string;
  course_title: string;
  course_description: string;
  difficulty_level: string;
  order: number;
  rationale: string;
}

// Status tracking interfaces
export interface StageStatusDto {
  status: 'not_started' | 'processing' | 'completed' | 'failed';
  start_time?: Date;
  end_time?: Date;
  duration_ms?: number;
  retry_count: number;
  error_details?: string;
}

export interface LearningPathStatusDto {
  orchestration_id: string;
  overall_status: 'pending' | 'processing' | 'completed' | 'failed';
  progress_percentage: number;
  current_stage: string;
  stage_1_metadata: StageStatusDto;
  stage_2_images: StageStatusDto;
  stage_3_courses: StageStatusDto & {
    metadata?: {
      courses_found: number;
    };
  };
  learning_path_id?: string;
  path_title?: string;
  description?: string;
  target_role_or_skill?: string;
  tags?: string[];
  banner_image_url?: string;
  thumbnail_image_url?: string;
  total_courses?: number;
  no_courses_found?: boolean;
  no_courses_reason?: string;
  total_duration_ms: number;
  started_at: Date;
  completed_at?: Date;
}

// Filter and list interfaces
export interface LearningPathFilterDto {
  status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  target_role_or_skill?: string;
  search?: string;
  skip?: number;
  limit?: number;
}

export interface LearningPathListResponseDto {
  items: LearningPathDto[];
  total: number;
}

// User input enhancement interface
export interface EnhanceUserInputDto {
  user_input: string;
  target_role_or_skill?: string;
}

export interface EnhancedUserInputDto {
  enhanced_input: string;
  suggestions: string[];
  key_concepts: string[];
}

export interface LearningPathMetricsDto {
  
total_active_courses: number;
  published: number;
  drafts: number;
  archived: number;
  total_enrolled: number;
  total_completed: number;
}

class LearningPathApiService {
  private baseUrl = '/learning-paths';

  // ==================== Learning Path Management ====================

  async listLearningPaths(
    filters: LearningPathFilterDto = {}
  ): Promise<LearningPathListResponseDto> {
    const response = await apiClient.get(this.baseUrl, { params: filters });
    return response.data.data;
  }

  async getLearningPathDropdown(): Promise<Array<{ _id: string; title: string }>> {
    const response = await apiClient.get(`${this.baseUrl}/learningpath`);
    return response.data.data;
  }

  async getLearningPathById(id: string): Promise<LearningPathDto> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  async createLearningPath(
    dto: CreateLearningPathDto
  ): Promise<LearningPathOrchestrationDto> {
    const response = await apiClient.post(this.baseUrl, dto);
    return response.data.data;
  }

  async publishLearningPath(id: string): Promise<LearningPathDto> {
    const response = await apiClient.put(`${this.baseUrl}/${id}/publish`);
    return response.data.data;
  }

  async updateLearningPathStatus(
    id: string,
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
  ): Promise<LearningPathDto> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/status`, { status });
    return response.data.data;
  }

  async deleteLearningPath(id: string): Promise<{
    success: boolean;
    can_delete: boolean;
    code: number;
    message: string;
    meta_data?: {
      active_users: any[];
    };
  }> {
    const response = await apiClient.delete(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  async updateLearningPath(
    id: string,
    dto: {
      title?: string;
      description?: string;
      target_role_or_skill?: string;
      targeted_audience?: string;
      difficulty_progression?: string;
      outcomes?: string[];
      tags?: string[];
      suggestions?: string[];
      banner_image_url?: string;
      thumbnail_image_url?: string;
      certificate_template_id?: string; // '' = back to the default template
      courses?: Array<{
        course_id: string;
        order: number;
        course_title?: string;
        course_description?: string;
        difficulty_level?: string;
        rationale?: string;
      }>;
    }
  ): Promise<LearningPathDto> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, dto);
    return response.data.data;
  }

  async getPublishedCourses(): Promise<Array<{
    _id: string;
    title: string;
    course_description?: string;
    difficulty_level?: string;
  }>> {
    const response = await apiClient.get(`${this.baseUrl}/utils/published-courses`);
    return response.data.data.courses;
  }

  async getPublishedLearningPaths(): Promise<Array<{
    _id: string;
    title: string;
  }>> {
    const response = await apiClient.get(`${this.baseUrl}/learningpath`);
    return response.data.data;
  }

  // ==================== Status Tracking ====================

  async getLearningPathStatus(
    orchestration_id: string
  ): Promise<LearningPathStatusDto> {
    const response = await apiClient.get(
      `${this.baseUrl}/${orchestration_id}/status`
    );
    return response.data.data;
  }

  async retryGeneration(
    orchestration_id: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.put(
      `${this.baseUrl}/${orchestration_id}/retry`
    );
    return response.data.data;
  }

  // ==================== Recent Orchestrations ====================

  async getRecentOrchestrations(params: {
    skip?: number;
    limit?: number;
  } = {}): Promise<{
    orchestrations: LearningPathOrchestrationDto[];
    total: number;
    pagination: {
      total: number;
      limit: number;
      skip: number;
      totalPages: number;
    };
  }> {
    const response = await apiClient.get(
      `${this.baseUrl}/utils/recent-orchestrations`,
      { params }
    );
    return response.data.data;
  }

  // ==================== AI Enhancement ====================

  async enhanceUserInput(
    dto: EnhanceUserInputDto
  ): Promise<EnhancedUserInputDto> {
    const response = await apiClient.post(
      `${this.baseUrl}/utils/enhance-input`,
      dto
    );
    return response.data.data;
  }

  // ==================== Metrics ====================

  async getMetrics(): Promise<LearningPathMetricsDto> {
    const response = await apiClient.get(`${this.baseUrl}/metrics`);
    return response.data.data;
  }
}

export const learningPathApiService = new LearningPathApiService();
